import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button } from '../common';
import ProblemPreview from '../ProblemPreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const BulkAddModal = ({
  isOpen,
  onClose,
  treeId,
  fetchWithTokenRefresh,
  fetchProblemCounts
}) => {
  const [searchLabel, setSearchLabel] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [totalAddAbleCount, setTotalAddAbleCount] = useState(0);
  const [previewProblemId, setPreviewProblemId] = useState(null);
  const [bulkAddResult, setBulkAddResult] = useState(null);

  // 문제 검색
  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    setBulkAddResult(null);
    try {
      // 검색어가 있는 경우와 없는 경우를 구분하여 URL 생성
      const url = searchLabel.trim() 
        ? `/api/referencefields/bulk-add?tree_id=${treeId}&specific_labels=${encodeURIComponent(searchLabel.trim())}`
        : `/api/referencefields/bulk-add?tree_id=${treeId}`;
      
      const response = await fetchWithTokenRefresh(url, { method: 'GET' });

      if (response?.status === 'success' && response?.data) {
        setSearchResults(response.data.problems || []);
        setTotalAddAbleCount(response.data.total_add_able_count || 0);
        setSelectedProblems(new Set(response.data.problems.map(p => p.problem_id)));
      } else {
        setSearchResults([]);
        setTotalAddAbleCount(0);
        alert('검색 결과가 없습니다.');
      }
    } catch (error) {
      console.error('문제 검색 실패:', error);
      alert('문제 검색에 실패했습니다.');
      setSearchResults([]);
      setTotalAddAbleCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchLabel, treeId, fetchWithTokenRefresh]);

  // 대규모 편성 실행
  const handleBulkAdd = useCallback(async () => {
    const problemIds = Array.from(selectedProblems);
    if (problemIds.length === 0) {
      alert('편성할 문제를 선택해주세요.');
      return;
    }

    setIsBulkAdding(true);
    setBulkAddResult(null);
    try {
      const response = await fetchWithTokenRefresh(
        '/api/referencefields/bulk-add',
        {
          method: 'POST',
          body: JSON.stringify({
            tree_id: parseInt(treeId),
            problem_ids: problemIds
          })
        }
      );

      // 400 에러 처리 (편성 가능한 문제가 없는 경우)
      if (response?.status === 'error' && response?.status_code === 400) {
        let errorMessage = response.message || '편성할 수 있는 문제가 없습니다.\n\n';
        
        if (response.data) {
          errorMessage += '상세 내용:\n';
          
          if (response.data.ocr_not_confirmed_list?.length > 0) {
            errorMessage += `❌ OCR 미확인: ${response.data.ocr_not_confirmed_list.length}개\n`;
          }
          if (response.data.already_added_list?.length > 0) {
            errorMessage += `⚠️ 이미 편성됨: ${response.data.already_added_list.length}개\n`;
          }
          if (response.data.label_fail_list?.length > 0) {
            errorMessage += `❌ 라벨 검증 실패: ${response.data.label_fail_list.length}개\n`;
          }
          if (response.data.not_found_db_list?.length > 0) {
            errorMessage += `❌ DB에 없음: ${response.data.not_found_db_list.length}개\n`;
          }
        }
        
        alert(errorMessage);
        return;
      }

      if (response?.status === 'success' && response?.data) {
        setBulkAddResult(response.data);
        
        // 성공한 문제들을 검색 결과에서 제거
        const successList = response.data.success_list || [];
        if (successList.length > 0) {
          setSearchResults(prev => prev.filter(p => !successList.includes(p.problem_id)));
          setSelectedProblems(new Set());
          // 문제 수 업데이트
          await fetchProblemCounts();
        }

        // 결과 메시지 생성
        let message = '';
        if (successList.length > 0) {
          message += `✅ ${successList.length}개 문제 편성 성공\n`;
        }
        if (response.data.already_added?.length > 0) {
          message += `⚠️ ${response.data.already_added.length}개 이미 편성됨\n`;
        }
        if (response.data.label_fail?.length > 0) {
          message += `❌ ${response.data.label_fail.length}개 라벨 검증 실패\n`;
        }
        if (response.data.ocr_not_confirmed?.length > 0) {
          message += `❌ ${response.data.ocr_not_confirmed.length}개 OCR 미확인\n`;
        }
        if (response.data.prob_not_found?.length > 0) {
          message += `❌ ${response.data.prob_not_found.length}개 문제 없음\n`;
        }

        if (message) {
          alert(message);
        }
      } else if (response?.status !== 'error') {
        // 400이 아닌 다른 에러
        alert('대규모 편성에 실패했습니다.');
      }
    } catch (error) {
      console.error('대규모 편성 실패:', error);
      alert('대규모 편성에 실패했습니다.');
    } finally {
      setIsBulkAdding(false);
    }
  }, [selectedProblems, treeId, fetchWithTokenRefresh, fetchProblemCounts]);

  // 문제 선택/해제
  const toggleProblemSelection = (problemId) => {
    setSelectedProblems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(problemId)) {
        newSet.delete(problemId);
      } else {
        newSet.add(problemId);
      }
      return newSet;
    });
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedProblems.size === searchResults.length) {
      setSelectedProblems(new Set());
    } else {
      setSelectedProblems(new Set(searchResults.map(p => p.problem_id)));
    }
  };

  // 라벨 파싱하여 표시
  const formatLabels = (labels) => {
    if (!labels) return '';
    const parts = labels.split(',');
    const displayParts = parts.slice(0, 3).join(', ');
    return parts.length > 3 ? `${displayParts}, ...` : displayParts;
  };

  return createPortal(
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="대규모 편성"
        size="xl"
        zIndex={9999950}  // ProblemPreview(9999960)보다 낮게 설정
        footer={
        <div className="flex justify-between w-full">
          <div className="text-sm text-gray-600">
            {searchResults.length > 0 && (
              <>
                검색: {searchResults.length}개 / 전체: {totalAddAbleCount}개
                {totalAddAbleCount > 500 && (
                  <span className="ml-2 text-orange-500">
                    (최대 500개만 조회됨)
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              닫기
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkAdd}
              disabled={isBulkAdding || selectedProblems.size === 0}
            >
              {isBulkAdding ? '편성 중...' : `선택한 ${selectedProblems.size}개 편성`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* 검색 영역 */}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="라벨 검색어 입력 (선택사항, 콤마로 AND 검색)"
            value={searchLabel}
            onChange={(e) => setSearchLabel(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 rounded-3xl border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none placeholder:text-sm"
          />
          <Button
            onClick={handleSearch}
            disabled={isLoading}
            variant="primary"
          >
            {isLoading ? '검색 중...' : '검색'}
          </Button>
        </div>

        {/* 편성 결과 메시지 */}
        {bulkAddResult && (
          <div className="rounded-lg bg-blue-50 p-3 text-sm">
            <div className="font-semibold mb-2">편성 결과:</div>
            {bulkAddResult.success_list?.length > 0 && (
              <div className="text-green-600">
                ✅ 성공: {bulkAddResult.success_list.length}개
              </div>
            )}
            {bulkAddResult.already_added?.length > 0 && (
              <div className="text-orange-600">
                ⚠️ 이미 편성됨: {bulkAddResult.already_added.length}개
              </div>
            )}
            {bulkAddResult.label_fail?.length > 0 && (
              <div className="text-red-600">
                ❌ 라벨 검증 실패: {bulkAddResult.label_fail.length}개
              </div>
            )}
          </div>
        )}

        {/* 검색 결과 테이블 */}
        {searchResults.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedProblems.size === searchResults.length}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium">전체 선택</span>
              </label>
              <span className="text-sm text-gray-600">
                {selectedProblems.size}개 선택됨
              </span>
            </div>

            <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-100">
                    <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">선택</th>
                    <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">문제 ID</th>
                    <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 bg-gray-100">라벨</th>
                    <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 hidden md:table-cell bg-gray-100">작성자</th>
                    <th className="px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-700 hidden md:table-cell bg-gray-100">생성일</th>
                    <th className="px-2 md:px-4 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100">미리보기</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {searchResults.map((problem) => (
                    <tr key={problem.problem_id} className="hover:bg-gray-50">
                      <td className="px-2 md:px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedProblems.has(problem.problem_id)}
                          onChange={() => toggleProblemSelection(problem.problem_id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-2 md:px-4 py-2 text-xs md:text-sm">{problem.problem_id}</td>
                      <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600">
                        <span title={problem.labels}>
                          {formatLabels(problem.labels)}
                        </span>
                      </td>
                      <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600 hidden md:table-cell">{problem.author}</td>
                      <td className="px-2 md:px-4 py-2 text-xs md:text-sm text-gray-600 hidden md:table-cell">
                        {new Date(problem.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-center">
                        <button
                          onClick={() => setPreviewProblemId(problem.problem_id)}
                          className="text-blue-500 hover:text-blue-700 text-xs md:text-sm"
                        >
                          보기
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 검색 결과가 없을 때 */}
        {!isLoading && searchResults.length === 0 && searchLabel && (
          <div className="text-center py-8 text-gray-500">
            검색 결과가 없습니다.
          </div>
        )}
      </div>
    </Modal>
    
    {/* 문제 미리보기 모달 - Modal 밖에 위치시켜 z-index 문제 해결 */}
    {previewProblemId && (
      <ProblemPreview
        isOpen={true}
        onClose={() => setPreviewProblemId(null)}
        problemIds={[previewProblemId]}
        showNumbers={false}
        correctList={[]}
      />
    )}
    </>,
    document.body
  );
};

export default BulkAddModal;
